/**
 * Stable error codes of Cost Services (Phase 1, ALC-4473). The copy lives next to the codes so a
 * code cannot ship without pt-BR and en text: `COST_SERVICE_ERROR_MESSAGES` is a total `Record`.
 * Classify errors by `code`, never by message text.
 *
 * Shape follows the Revenue Tax pattern (codes + bilingual copy in `@alinea/types`, one thrown
 * class, one exception filter). The class lives here, not in `@erp/services`, because the pure
 * knob catalog in this package throws it too and the later slices (S2 recognition hook, S3 bill
 * approval, S4 close-out) throw it from the API and from services alike.
 */
export const CostServiceErrorCode = {
  /** A PATCH named a knob that is not in the Phase 1 catalog. */
  COST_SERVICE_KNOB_UNKNOWN: 'COST_SERVICE_KNOB_UNKNOWN',
  /** A knob value is malformed, out of its domain, or not writable through this door. */
  COST_SERVICE_KNOB_VALUE_INVALID: 'COST_SERVICE_KNOB_VALUE_INVALID',
  /** The tolerance is not EXACT, an amount or a percentage. */
  COST_SERVICE_TOLERANCE_INVALID: 'COST_SERVICE_TOLERANCE_INVALID',
  /** Activation: the Cost Service has no vendor. */
  COST_SERVICE_VENDOR_REQUIRED: 'COST_SERVICE_VENDOR_REQUIRED',
  /** Activation: the Cost Service has no rate. */
  COST_SERVICE_RATE_REQUIRED: 'COST_SERVICE_RATE_REQUIRED',
  /** Activation (D34): the Cost Service has no cost (expense) account. */
  COST_SERVICE_COST_ACCOUNT_REQUIRED: 'COST_SERVICE_COST_ACCOUNT_REQUIRED',
  /** Activation (D34): the Cost Service has no liability (provision) account. */
  COST_SERVICE_LIABILITY_ACCOUNT_REQUIRED: 'COST_SERVICE_LIABILITY_ACCOUNT_REQUIRED',
  /** Activation (D34): the Cost Service has no variance account. */
  COST_SERVICE_VARIANCE_ACCOUNT_REQUIRED: 'COST_SERVICE_VARIANCE_ACCOUNT_REQUIRED',
  /** Activation: the liability account is an AP control account (ADR 0016 / ADR 0024 rule). */
  COST_SERVICE_LIABILITY_ACCOUNT_IS_AP_CONTROL: 'COST_SERVICE_LIABILITY_ACCOUNT_IS_AP_CONTROL',
  /** Activation or attachment: the active-to date is before the active-from date. */
  COST_SERVICE_ACTIVE_RANGE_INVALID: 'COST_SERVICE_ACTIVE_RANGE_INVALID',
  /** Create from template: the template name is not one that ships. */
  COST_SERVICE_TEMPLATE_UNKNOWN: 'COST_SERVICE_TEMPLATE_UNKNOWN',
  COST_SERVICE_NOT_FOUND: 'COST_SERVICE_NOT_FOUND',
  /** Create from template: the entity does not exist for this tenant. */
  COST_SERVICE_ENTITY_NOT_FOUND: 'COST_SERVICE_ENTITY_NOT_FOUND',
  /** Create from template: the entity already has a Cost Service with that name. */
  COST_SERVICE_NAME_TAKEN: 'COST_SERVICE_NAME_TAKEN',
  /** Activate on a service that is already active, or deactivate on one that is not. */
  COST_SERVICE_STATUS_INVALID: 'COST_SERVICE_STATUS_INVALID',
  /** The vendor knob names a vendor that does not exist for this tenant. */
  COST_SERVICE_VENDOR_NOT_FOUND: 'COST_SERVICE_VENDOR_NOT_FOUND',
  /** An account knob names an account that is missing, inactive, not postable or of the wrong type. */
  COST_SERVICE_ACCOUNT_INVALID: 'COST_SERVICE_ACCOUNT_INVALID',
  /** Attach: the billing service does not exist in this tenant and entity. */
  COST_SERVICE_BILLING_SERVICE_NOT_FOUND: 'COST_SERVICE_BILLING_SERVICE_NOT_FOUND',
  COST_SERVICE_ATTACHMENT_NOT_FOUND: 'COST_SERVICE_ATTACHMENT_NOT_FOUND',
  /** Attach: the same Cost Service is already attached to the billing service in an overlapping window. */
  COST_SERVICE_ATTACHMENT_OVERLAP: 'COST_SERVICE_ATTACHMENT_OVERLAP',
  /**
   * PATCH baseType: a live link (not ended) still carries a rate override written in the current
   * base type's form, so the type cannot change until those links end.
   */
  COST_SERVICE_BASE_TYPE_LOCKED: 'COST_SERVICE_BASE_TYPE_LOCKED',
  /** Edit a link: the request changes nothing it may change (no rate, start or end given). */
  COST_SERVICE_ATTACHMENT_CHANGE_REQUIRED: 'COST_SERVICE_ATTACHMENT_CHANGE_REQUIRED',
  /**
   * Edit a link: widening or reopening an ended link that has its own rate must say the rate again
   * (it may have been written for another base type).
   */
  COST_SERVICE_RATE_OVERRIDE_REQUIRED_TO_REOPEN: 'COST_SERVICE_RATE_OVERRIDE_REQUIRED_TO_REOPEN',
  /**
   * D24: a new rate on a link that already recognised costs needs the day it starts
   * (`effectiveFrom`): the link ends the day before and a new link carries the new rate.
   */
  COST_SERVICE_RATE_EFFECTIVE_FROM_REQUIRED: 'COST_SERVICE_RATE_EFFECTIVE_FROM_REQUIRED',
  /**
   * D24 guard (owner, 2026-10-10): the new rate would start on or before a month the link already
   * posted (a live, not reversed, POSTED provision; D30: or a live PENDING one stuck on a setup
   * gap, never a D22 month not due yet). Details carry `lastPostedThrough` and
   * `earliestAllowed` (YYYY-MM-DD). Past months are fixed with reversals and manual journals.
   */
  COST_SERVICE_RATE_EFFECTIVE_FROM_BEFORE_POSTED: 'COST_SERVICE_RATE_EFFECTIVE_FROM_BEFORE_POSTED',
  /** D24 guard: the new rate would start in a closed period. Details carry `earliestAllowed`. */
  COST_SERVICE_RATE_EFFECTIVE_FROM_IN_CLOSED_PERIOD:
    'COST_SERVICE_RATE_EFFECTIVE_FROM_IN_CLOSED_PERIOD',
  /**
   * D22 schedule: "Reconhecimento do valor fixo" (once, or spread over N months) cannot change
   * while the service has a live posted fixed provision. Posted history is never rewritten.
   */
  COST_SERVICE_FIXED_RECOGNITION_LOCKED: 'COST_SERVICE_FIXED_RECOGNITION_LOCKED',
  /**
   * D39: "Distribuir em N meses" does not apply to "Valor fixo por mês" (FIXED_PER_MONTH), which
   * is already one charge per month of revenue.
   */
  COST_SERVICE_FIXED_SCHEDULE_NOT_FOR_MONTHLY: 'COST_SERVICE_FIXED_SCHEDULE_NOT_FOR_MONTHLY',
  /**
   * D40: "Cobrar de novo na renovação" only applies to "Valor fixo por contrato" (a percentage
   * follows the revenue and "Valor fixo por mês" is already charged every month, in every term).
   */
  COST_SERVICE_RENEWAL_CHARGE_NOT_FOR_BASE: 'COST_SERVICE_RENEWAL_CHARGE_NOT_FOR_BASE',
  /**
   * D30: the service rate cannot change in place while a link that inherits it (no rate of its
   * own; live or ENDED, fold L re-check) has a posted or stuck pending provision — a late catch-up
   * would book an earlier month at the new rate. Change the rate on the link (edit dialog) with a
   * start date. Details carry `attachmentIds`.
   */
  COST_SERVICE_RATE_LOCKED_USE_LINK_CHANGE: 'COST_SERVICE_RATE_LOCKED_USE_LINK_CHANGE',
  /** Read or heal: the cost recognition entry (provision) does not exist in this tenant. */
  COST_ENTRY_NOT_FOUND: 'COST_ENTRY_NOT_FOUND',
  /** Read: a filter of the provision list is malformed (status, period, ids). */
  COST_ENTRY_FILTER_INVALID: 'COST_ENTRY_FILTER_INVALID',
  /** The recognition hook was handed a revenue journal that does not exist or is not POSTED. */
  /**
   * r4 (accounting #6): an invoice line names a contract but no contract item. No writer produces
   * one (every path stamps both), so it is corrupt data: recognition refuses it rather than invent
   * a per-line key for a "per month" cost.
   */
  COST_RECOGNITION_INVOICE_LINE_ITEM_MISSING: 'COST_RECOGNITION_INVOICE_LINE_ITEM_MISSING',
  COST_RECOGNITION_SOURCE_JOURNAL_NOT_FOUND: 'COST_RECOGNITION_SOURCE_JOURNAL_NOT_FOUND',
  // ALC-4476 (S3) — bill side. The first six are the approval checks, in the spec's order; the
  // scorer (`@alinea/core/cost-matching` COST_SELECTION_VALIDATION_ORDER) returns the same strings.
  /** A line paying provisioned cost has no provision selected. */
  COST_LINE_PROVISION_WITHOUT_ENTRIES: 'COST_LINE_PROVISION_WITHOUT_ENTRIES',
  /** The amounts applied add up to more than the bill line. */
  COST_MATCH_EXCEEDS_BILL_LINE: 'COST_MATCH_EXCEEDS_BILL_LINE',
  /** An amount applied is more than what is still open on the provision. */
  COST_MATCH_EXCEEDS_ENTRY_BALANCE: 'COST_MATCH_EXCEEDS_ENTRY_BALANCE',
  /** A selected provision is owed to another vendor than the bill's. */
  COST_MATCH_VENDOR_MISMATCH: 'COST_MATCH_VENDOR_MISMATCH',
  /** A selected provision is settled, reversed or gone. */
  COST_ENTRY_NOT_OPEN: 'COST_ENTRY_NOT_OPEN',
  /**
   * D22 schedule: close-out of a plan month that is not due yet (PENDING, SCHEDULED_NOT_DUE).
   * Its own month posts it; the plan stops only with a full revenue reversal of the contract.
   */
  COST_ENTRY_SCHEDULED_NOT_DUE: 'COST_ENTRY_SCHEDULED_NOT_DUE',
  /** The difference between the line and the provisions is beyond the tolerance (warning + consent, D12). */
  COST_MATCH_VARIANCE_OUT_OF_TOLERANCE: 'COST_MATCH_VARIANCE_OUT_OF_TOLERANCE',
  /** The line destination asked for does not exist in this release (COST_POOL is Phase 3). */
  COST_LINE_DESTINATION_NOT_AVAILABLE: 'COST_LINE_DESTINATION_NOT_AVAILABLE',
  /** Undo or read: the match does not exist in this tenant (or was already undone). */
  COST_MATCH_NOT_FOUND: 'COST_MATCH_NOT_FOUND',
  /** Undo without a reason. */
  COST_MATCH_UNDO_REASON_REQUIRED: 'COST_MATCH_UNDO_REASON_REQUIRED',
  /** The destination or the selection of a line can only change while the bill is a draft. */
  AP_BILL_NOT_EDITABLE: 'AP_BILL_NOT_EDITABLE',
  /**
   * D48: a bill past draft that holds a generated "whole bill" line refuses an edit to its total,
   * currency, vendor or entity. The line no longer follows the bill there, so approval would
   * relieve the old amount.
   */
  AP_BILL_GENERATED_LINE_HEADER_LOCKED: 'AP_BILL_GENERATED_LINE_HEADER_LOCKED',
  /** D44 "Remover item": a bill keeps at least one line. */
  AP_BILL_LAST_LINE_ITEM: 'AP_BILL_LAST_LINE_ITEM',
  /** D44 "Remover item": the line has posted history (provision match, reservation, journal). */
  AP_BILL_LINE_ITEM_HAS_HISTORY: 'AP_BILL_LINE_ITEM_HAS_HISTORY',
  /**
   * D45f: lines of a bill past approval (APPROVED, PAID, VOID…) cannot be added, changed or
   * removed. Kept a 400 (it replaced a plain 400 with no code).
   */
  AP_BILL_LINE_ITEMS_LOCKED: 'AP_BILL_LINE_ITEMS_LOCKED',
  /** The bill or the bill line in the path does not exist in this tenant. */
  COST_LINE_NOT_FOUND: 'COST_LINE_NOT_FOUND',
  /** A selected provision is in another currency than the bill line. */
  COST_MATCH_CURRENCY_MISMATCH: 'COST_MATCH_CURRENCY_MISMATCH',
  /** A selection is malformed: empty, a repeated provision, or an amount that is not a positive decimal. */
  COST_MATCH_SELECTION_INVALID: 'COST_MATCH_SELECTION_INVALID',
  /** Confirm: the suggested set named is no longer among the suggestions for the line. */
  COST_MATCH_SET_NOT_FOUND: 'COST_MATCH_SET_NOT_FOUND',
  /**
   * A "pays provision" line of a multi-line bill has no classification of its own to replace.
   * D44: a bill whose lines all pay a provision may share one bill-level classification when it
   * is the only one and the lines add up to the bill total. It still fires on a mixed bill, on
   * 2+ bill-level classifications, and when the lines do not add up to the total.
   */
  COST_LINE_CLASSIFICATION_REQUIRED: 'COST_LINE_CLASSIFICATION_REQUIRED',
  /**
   * D45e: an all-cost bill has two or more bill-level classifications, so no single one can be
   * shared by its lines (D44).
   */
  COST_LINE_SHARED_CLASSIFICATION_MULTIPLE: 'COST_LINE_SHARED_CLASSIFICATION_MULTIPLE',
  /**
   * D45e: an all-cost bill shares one bill-level classification, but its lines do not add up to
   * the bill total, so the per-line shares of the classification would not be the line amounts.
   */
  COST_LINE_SHARED_CLASSIFICATION_SUM_MISMATCH: 'COST_LINE_SHARED_CLASSIFICATION_SUM_MISMATCH',
  /** A "pays provision" line cannot post together with a deferral, a capitalisation, a lease, a project, a collaborator split, a client-reimbursable bill or PIS/COFINS credits. */
  COST_LINE_POSTING_INCOMPATIBLE: 'COST_LINE_POSTING_INCOMPATIBLE',
  /** The provision, variance or FX account of the match has no account in the posting book's chart. */
  COST_MATCH_ACCOUNT_NOT_MAPPED: 'COST_MATCH_ACCOUNT_NOT_MAPPED',
  /** A later match (story 48) needs a bill that is approved and posted. */
  COST_MATCH_BILL_NOT_POSTED: 'COST_MATCH_BILL_NOT_POSTED',
  /**
   * A later match (story 48) asked where the reclass journal cannot be posted (ALC-4686: the MCP
   * tool `cost_matches_create` on an approved bill). Matched on the bill screen instead.
   */
  COST_MATCH_AFTER_APPROVAL_UNAVAILABLE: 'COST_MATCH_AFTER_APPROVAL_UNAVAILABLE',
  /** Later match (story 48) or undo: the period of today is not open in a book of the bill. */
  COST_MATCH_PERIOD_NOT_OPEN: 'COST_MATCH_PERIOD_NOT_OPEN',
  /** D29 (fold L re-check): an approval-time prepaid reclass finds no OPEN period from its provision day through the current month in a book of the bill. */
  COST_PREPAID_RECLASS_NO_OPEN_PERIOD: 'COST_PREPAID_RECLASS_NO_OPEN_PERIOD',
  /** ADR 0016: the bill already settled an expense accrual, so its expense cannot also relieve a provision. */
  COST_MATCH_LINE_SETTLED_BY_ACCRUAL: 'COST_MATCH_LINE_SETTLED_BY_ACCRUAL',
  /** A revenue reversal must mirror a posted cost, but the reversal's period is not open. */
  COST_REVERSAL_PERIOD_NOT_OPEN: 'COST_REVERSAL_PERIOD_NOT_OPEN',
  /** A cost (or FX) account of the provision has no counterpart in the reversal book's chart. */
  COST_REVERSAL_ACCOUNT_NOT_MAPPED: 'COST_REVERSAL_ACCOUNT_NOT_MAPPED',
  // ALC-4477 (S4) — close-out of a provision the vendor will not bill.
  /** Close-out without a reason. */
  COST_PROVISION_CLOSE_REASON_REQUIRED: 'COST_PROVISION_CLOSE_REASON_REQUIRED',
  /** Close-out: today's date has no open period in the provision's book. */
  COST_PROVISION_CLOSE_PERIOD_NOT_OPEN: 'COST_PROVISION_CLOSE_PERIOD_NOT_OPEN',
  /** Close-out: a draft bill line still selects the provision (release it on the bill first). */
  COST_PROVISION_CLOSE_RESERVED_BY_BILL: 'COST_PROVISION_CLOSE_RESERVED_BY_BILL',
  /** Close-out: a cost, provision or FX account has no counterpart in today's chart of accounts. */
  COST_PROVISION_CLOSE_ACCOUNT_NOT_MAPPED: 'COST_PROVISION_CLOSE_ACCOUNT_NOT_MAPPED',
  /**
   * D21: a revenue reversal reduced a line's provision relief (residual match). Edit-and-repost,
   * reclassification and status regression of the bill are refused until the line's relief is
   * undone; voiding stays allowed.
   */
  COST_MATCH_RELIEF_REDUCED_BY_REVERSAL: 'COST_MATCH_RELIEF_REDUCED_BY_REVERSAL',
  /**
   * P1-5: a vendor bill relieved the provision while a revenue reversal of it was starting. The
   * reversal locks the bills holding the provision before the provision itself; a bill that
   * appeared in between is refused rather than locked out of order. Retryable as is.
   */
  COST_REVERSAL_RETRY_BILL_RELIEF_CHANGED: 'COST_REVERSAL_RETRY_BILL_RELIEF_CHANGED',
  /**
   * P2-4 / D23: the bill selects a provision whose cost journal has not posted yet (PENDING): there
   * is no provision balance to relieve. A provision posted after the bill date is fine; its relief
   * posts on the provision's own cost-journal day.
   */
  COST_MATCH_RELIEF_BEFORE_RECOGNITION: 'COST_MATCH_RELIEF_BEFORE_RECOGNITION',
  /**
   * D23: the bill pays a provision whose cost journal posts after the bill date, so that part has
   * to sit on a prepaid asset until the provision posts, and the entity's chart has no account on
   * the AP_COST_SERVICE_PREPAID system slot (or it is not a postable asset). Approval is refused;
   * nothing is guessed.
   */
  COST_SERVICE_PREPAID_ACCOUNT_NOT_CONFIGURED: 'COST_SERVICE_PREPAID_ACCOUNT_NOT_CONFIGURED',
  // D26 — a bill approved before the provision it pays exists waits on prepaid (awaiting).
  /**
   * D26: an expected (not yet recognised) provision row of a bill line names a Cost Service that
   * is not this bill's: another entity, another vendor, another currency, or not active.
   */
  COST_AWAITING_SELECTION_INVALID: 'COST_AWAITING_SELECTION_INVALID',
  /** D26: the awaiting reservation (or the line's awaiting part) was not found. */
  COST_AWAITING_RESERVATION_NOT_FOUND: 'COST_AWAITING_RESERVATION_NOT_FOUND',
  /** D26: the awaiting reservation was already consumed, cancelled, closed or voided. */
  COST_AWAITING_RESERVATION_NOT_OPEN: 'COST_AWAITING_RESERVATION_NOT_OPEN',
  /**
   * D28: an expected (not yet recognised) month was picked, but that Cost Service and vendor
   * already have a provision posted for the month — select the provision itself.
   */
  COST_AWAITING_MONTH_ALREADY_PROVISIONED: 'COST_AWAITING_MONTH_ALREADY_PROVISIONED',
  /**
   * D28: an expected month was picked that will never post: outside the Cost Service's active
   * window, a plan month cancelled by a revenue reversal, or a one-off fixed charge that already
   * covered every linked contract.
   */
  COST_AWAITING_MONTH_WILL_NOT_POST: 'COST_AWAITING_MONTH_WILL_NOT_POST',
  // D27 — a bill line split across the vendor's cost services.
  /**
   * D27: a line-level cost service choice (one service or a split) was asked for, but the bill's
   * vendor has fewer than two ACTIVE cost services for the entity, or a named service is not one
   * of them (another vendor, another entity, not active on the bill date).
   */
  COST_LINE_SERVICE_CHOICE_NOT_AVAILABLE: 'COST_LINE_SERVICE_CHOICE_NOT_AVAILABLE',
  /**
   * D27: the split is malformed: fewer than two parts, a service named twice, or a part amount
   * that is not a positive decimal.
   */
  COST_LINE_SPLIT_INVALID: 'COST_LINE_SPLIT_INVALID',
  /** D27: the split parts do not add up exactly to the bill line amount. */
  COST_LINE_SPLIT_SUM_MISMATCH: 'COST_LINE_SPLIT_SUM_MISMATCH',
  /** D27: the provisions selected for one service of a split add up to more than that part. */
  COST_LINE_SPLIT_PART_EXCEEDED: 'COST_LINE_SPLIT_PART_EXCEEDED',
  /** D27: a selected provision belongs to a cost service the line's choice or split leaves out. */
  COST_LINE_SERVICE_CHOICE_MISMATCH: 'COST_LINE_SERVICE_CHOICE_MISMATCH',
  // D43 — the automatic (FIFO) relief of a "Serviço de custo" line.
  /**
   * D43: the line is "Serviço de custo" but the bill's vendor has no ACTIVE cost service in the
   * entity on the bill date, so there is nothing to relieve automatically.
   */
  COST_LINE_FIFO_NO_COST_SERVICE: 'COST_LINE_FIFO_NO_COST_SERVICE',
  /**
   * D43: the line is larger than every open provision of its cost service and no later month of
   * the service can still post a provision to take the excess as prepaid (D23/D26).
   */
  COST_LINE_FIFO_EXCESS_NO_MONTH: 'COST_LINE_FIFO_EXCESS_NO_MONTH',
  // D45 — alerts, never thrown: the approval shows them and posts (warn + consent, consent
  // recorded, never required).
  /**
   * D45a (alert): the vendor has no ACTIVE cost service on the bill date, but an ended or paused
   * service still has open provisions; the line relieves them, oldest competency first.
   */
  COST_LINE_FIFO_SERVICE_INACTIVE_OPEN_PROVISIONS:
    'COST_LINE_FIFO_SERVICE_INACTIVE_OPEN_PROVISIONS',
  /**
   * D45b (alert): the line passes the open provisions and no later month takes the rest; the rest
   * posts to the expense account of the line's classification.
   */
  COST_LINE_FIFO_EXCESS_TO_EXPENSE: 'COST_LINE_FIFO_EXCESS_TO_EXPENSE',
  /** D46-1: alert (never thrown) — a same-month excess posted to variance, out of tolerance. */
  COST_LINE_FIFO_EXCESS_TO_VARIANCE: 'COST_LINE_FIFO_EXCESS_TO_VARIANCE',
  /**
   * D46-1: a same-month excess must post to the variance account of the provision it relieves,
   * and the provision has none (pre-flight refusal; the approval would fail with COST_MATCH_ACCOUNT_NOT_MAPPED).
   */
  COST_LINE_FIFO_VARIANCE_ACCOUNT_NOT_CONFIGURED: 'COST_LINE_FIFO_VARIANCE_ACCOUNT_NOT_CONFIGURED',
  // D43b — the service split of a "Serviço de custo" line.
  /**
   * D43b: a service of the line's split belongs to the bill's vendor and entity but is not ACTIVE
   * on the bill date (draft, paused, ended or not started yet).
   */
  COST_LINE_SERVICE_INACTIVE: 'COST_LINE_SERVICE_INACTIVE',
  /** D43b: a service of the line's split belongs to another vendor or another entity (or is unknown). */
  COST_LINE_SERVICE_OTHER_VENDOR: 'COST_LINE_SERVICE_OTHER_VENDOR',
} as const;

export type CostServiceErrorCode = (typeof CostServiceErrorCode)[keyof typeof CostServiceErrorCode];

export interface CostServiceCopy {
  readonly ptBR: string;
  readonly en: string;
}

/**
 * Actionable copy for every code: what happened, why, what to do. No enum names, HTTP codes or
 * table names (docs/agents/ux-writing.md section 7.1).
 */
export const COST_SERVICE_ERROR_MESSAGES: Readonly<Record<CostServiceErrorCode, CostServiceCopy>> =
  Object.freeze({
    COST_SERVICE_KNOB_UNKNOWN: {
      ptBR: 'Esse campo não existe no serviço de custo desta versão, então nada foi alterado. Use apenas os campos listados na configuração do serviço.',
      en: 'That setting does not exist on a Cost Service in this release, so nothing was changed. Use only the settings listed on the service.',
    },
    COST_SERVICE_KNOB_VALUE_INVALID: {
      ptBR: 'O valor informado não é válido para esse campo, então nenhuma alteração foi salva. Corrija o valor e tente de novo.',
      en: 'The value is not valid for that setting, so no change was saved. Fix the value and try again.',
    },
    COST_SERVICE_TOLERANCE_INVALID: {
      ptBR: 'A tolerância deve ser "Exato", um valor (por exemplo 10,50) ou uma porcentagem entre 0% e 100% (por exemplo 1%). Nenhuma alteração foi salva.',
      en: 'The tolerance must be "Exact", an amount (for example 10.50) or a percentage between 0% and 100% (for example 1%). No change was saved.',
    },
    COST_SERVICE_VENDOR_REQUIRED: {
      ptBR: 'O serviço de custo não pode ser ativado sem fornecedor, porque o custo provisionado precisa ser devido a alguém. Escolha o fornecedor e ative de novo.',
      en: 'The Cost Service cannot be activated without a vendor, because the provisioned cost must be owed to someone. Choose the vendor and activate again.',
    },
    COST_SERVICE_RATE_REQUIRED: {
      ptBR: 'O serviço de custo não pode ser ativado sem a taxa do custo (porcentagem da receita ou valor fixo). Informe a taxa e ative de novo.',
      en: 'The Cost Service cannot be activated without a cost rate (a percentage of revenue or a fixed amount). Enter the rate and activate again.',
    },
    COST_SERVICE_COST_ACCOUNT_REQUIRED: {
      ptBR: 'O serviço de custo não pode ser ativado sem a conta de custo, porque cada provisão lança o custo nessa conta. Defina a conta de custo e ative de novo.',
      en: 'The Cost Service cannot be activated without a cost account, because every provision books the cost to that account. Set the cost account and activate again.',
    },
    COST_SERVICE_LIABILITY_ACCOUNT_REQUIRED: {
      ptBR: 'O serviço de custo não pode ser ativado sem a conta de passivo, porque cada provisão fica em aberto nessa conta até a nota do fornecedor chegar. Defina a conta de passivo e ative de novo.',
      en: 'The Cost Service cannot be activated without a liability account, because every provision stays open in that account until the vendor invoice arrives. Set the liability account and activate again.',
    },
    COST_SERVICE_VARIANCE_ACCOUNT_REQUIRED: {
      ptBR: 'O serviço de custo não pode ser ativado sem a conta de variação, porque a diferença entre a provisão e a nota do fornecedor é lançada nessa conta. Defina a conta de variação e ative de novo.',
      en: 'The Cost Service cannot be activated without a variance account, because the difference between the provision and the vendor invoice is booked to that account. Set the variance account and activate again.',
    },
    COST_SERVICE_LIABILITY_ACCOUNT_IS_AP_CONTROL: {
      ptBR: 'A conta de passivo escolhida é a conta de fornecedores (contas a pagar), que guarda apenas dívidas já documentadas. Escolha uma conta própria de provisão de custos e ative de novo.',
      en: 'The liability account you chose is the vendor payables (AP control) account, which holds documented payables only. Choose a dedicated cost provision account and activate again.',
    },
    COST_SERVICE_ACTIVE_RANGE_INVALID: {
      ptBR: 'A data final de vigência é anterior à data inicial. Ajuste as datas para que o período termine no mesmo dia ou depois do início.',
      en: 'The end of the active period is before its start. Adjust the dates so the period ends on or after the day it starts.',
    },
    COST_SERVICE_TEMPLATE_UNKNOWN: {
      ptBR: 'Esse modelo de serviço de custo não existe nesta versão. Hoje só o modelo "Royalty" está disponível.',
      en: 'That Cost Service template does not exist in this release. Only the "Royalty" template is available today.',
    },
    COST_SERVICE_NOT_FOUND: {
      ptBR: 'O serviço de custo não foi encontrado. Atualize a lista e escolha outro.',
      en: 'The Cost Service was not found. Refresh the list and choose another.',
    },
    COST_SERVICE_ENTITY_NOT_FOUND: {
      ptBR: 'A entidade informada não foi encontrada. Escolha uma entidade existente.',
      en: 'The entity was not found. Choose an existing entity.',
    },
    COST_SERVICE_NAME_TAKEN: {
      ptBR: 'Já existe um serviço de custo com esse nome nesta entidade. Escolha outro nome.',
      en: 'A Cost Service with that name already exists in this entity. Choose another name.',
    },
    COST_SERVICE_STATUS_INVALID: {
      ptBR: 'O serviço de custo não está na situação certa para essa ação: só é possível ativar um serviço em rascunho ou inativo e desativar um serviço ativo.',
      en: 'The Cost Service is not in the right state for this action: only a draft or inactive service can be activated, and only an active service can be deactivated.',
    },
    COST_SERVICE_VENDOR_NOT_FOUND: {
      ptBR: 'O fornecedor informado não foi encontrado. Escolha um fornecedor cadastrado.',
      en: 'The vendor was not found. Choose a registered vendor.',
    },
    COST_SERVICE_ACCOUNT_INVALID: {
      ptBR: 'A conta informada não pode ser usada aqui: ela não existe, está inativa, não aceita lançamentos ou não é do tipo esperado para esse campo. Escolha outra conta.',
      en: 'The account cannot be used here: it does not exist, is inactive, does not accept postings or is not the type this setting expects. Choose another account.',
    },
    COST_SERVICE_BILLING_SERVICE_NOT_FOUND: {
      ptBR: 'O serviço de faturamento informado não foi encontrado nesta entidade. Escolha um serviço de faturamento existente.',
      en: 'The billing service was not found in this entity. Choose an existing billing service.',
    },
    COST_SERVICE_ATTACHMENT_NOT_FOUND: {
      ptBR: 'O vínculo entre o serviço de custo e o serviço de faturamento não foi encontrado. Atualize a tela e tente de novo.',
      en: 'The link between the Cost Service and the billing service was not found. Refresh and try again.',
    },
    COST_SERVICE_ATTACHMENT_OVERLAP: {
      ptBR: 'Esse serviço de custo já está vinculado a esse serviço de faturamento em um período que se sobrepõe ao informado. Encerre o vínculo atual ou escolha outras datas.',
      en: 'This Cost Service is already linked to this billing service in a period that overlaps the one you gave. End the current link or choose other dates.',
    },
    COST_SERVICE_ATTACHMENT_CHANGE_REQUIRED: {
      ptBR: 'Nada para alterar no vínculo: informe a nova taxa própria, o novo início ou o novo término.',
      en: 'Nothing to change on the link: give the new own rate, the new start or the new end.',
    },
    COST_SERVICE_RATE_OVERRIDE_REQUIRED_TO_REOPEN: {
      ptBR: 'Este vínculo já terminou e tem taxa própria. Para estender ou reabrir, informe a taxa de novo no mesmo pedido (ela pode ter sido escrita para outro tipo de base).',
      en: 'This link has ended and has its own rate. To extend or reopen it, give the rate again in the same request (it may have been written for another base type).',
    },
    COST_SERVICE_RATE_EFFECTIVE_FROM_REQUIRED: {
      ptBR: 'Este vínculo já provisionou custos, então a nova taxa precisa de uma data de início. O vínculo atual termina no dia anterior e um novo vínculo passa a valer com a nova taxa; o que já foi provisionado não muda.',
      en: 'This link has already provisioned costs, so the new rate needs a start date. The current link ends the day before and a new link carries the new rate from that day; what was already provisioned does not change.',
    },
    COST_SERVICE_RATE_EFFECTIVE_FROM_BEFORE_POSTED: {
      ptBR: 'Já existem provisões lançadas ou pendentes até {lastPostedThrough}. Escolha uma data a partir de {earliestAllowed}. Para corrigir meses lançados, use estornos e lançamentos manuais.',
      en: 'Provisions are already posted or pending through {lastPostedThrough}. Choose a date from {earliestAllowed} on. To fix posted months, use reversals and manual journals.',
    },
    COST_SERVICE_RATE_EFFECTIVE_FROM_IN_CLOSED_PERIOD: {
      ptBR: 'A data de início da nova taxa cai em um período fechado. Escolha uma data a partir de {earliestAllowed}. Para corrigir meses fechados, use estornos e lançamentos manuais.',
      en: 'The start date of the new rate falls in a closed period. Choose a date from {earliestAllowed} on. To fix closed months, use reversals and manual journals.',
    },
    COST_SERVICE_FIXED_RECOGNITION_LOCKED: {
      ptBR: 'O reconhecimento do valor fixo não pode mudar porque este serviço já tem provisões lançadas com a regra atual. Para corrigir meses lançados, use estornos e lançamentos manuais.',
      en: 'How the fixed amount is recognised cannot change because this service already has provisions posted under the current rule. To fix posted months, use reversals and manual journals.',
    },
    COST_SERVICE_FIXED_SCHEDULE_NOT_FOR_MONTHLY: {
      ptBR: '"Distribuir em N meses" não se aplica a "Valor fixo por mês": esse valor já é cobrado uma vez por mês de receita reconhecida. Deixe "De uma vez" ou mude a base para "Valor fixo por contrato".',
      en: '"Spread over N months" does not apply to "Fixed amount per month": that amount is already charged once per month of recognised revenue. Keep "At once" or change the base to "Fixed amount per contract".',
    },
    COST_SERVICE_RENEWAL_CHARGE_NOT_FOR_BASE: {
      ptBR: '"Cobrar de novo na renovação" só se aplica a "Valor fixo por contrato". Com "Valor fixo por mês" o valor já é cobrado todo mês, e um percentual acompanha a receita. Desligue a opção ou mude a base para "Valor fixo por contrato".',
      en: '"Charge again on renewal" only applies to "Fixed amount per contract". With "Fixed amount per month" the amount is already charged every month, and a percentage follows the revenue. Turn the option off or change the base to "Fixed amount per contract".',
    },
    COST_SERVICE_RATE_LOCKED_USE_LINK_CHANGE: {
      ptBR: 'A taxa do serviço não pode mudar aqui porque um vínculo que usa essa taxa já tem provisões lançadas ou pendentes. Mude a taxa no vínculo ("Editar" em Serviços de faturamento) e informe a partir de quando a nova taxa vale; os meses já provisionados não mudam.',
      en: 'The service rate cannot change here because a link that uses it already has posted or pending provisions. Change the rate on the link ("Edit" under Billing services) and give the date the new rate starts; months already provisioned do not change.',
    },
    COST_SERVICE_BASE_TYPE_LOCKED: {
      ptBR: 'O tipo de base não pode ser trocado enquanto algum vínculo ativo tiver uma taxa própria, porque essa taxa foi escrita no tipo atual. Encerre esses vínculos e tente de novo.',
      en: 'The base type cannot be changed while a live link carries its own rate, because that rate was written for the current type. End those links and try again.',
    },
    COST_ENTRY_NOT_FOUND: {
      ptBR: 'O custo provisionado não foi encontrado. Atualize a lista e escolha outro.',
      en: 'The provisioned cost was not found. Refresh the list and choose another.',
    },
    COST_ENTRY_FILTER_INVALID: {
      ptBR: 'Um dos filtros da lista de custos provisionados não é válido (situação, competência no formato AAAA-MM ou identificador). Corrija o filtro e tente de novo.',
      en: 'One of the provisioned cost filters is not valid (status, period as YYYY-MM, or an id). Fix the filter and try again.',
    },
    COST_RECOGNITION_INVOICE_LINE_ITEM_MISSING: {
      ptBR: 'Uma linha da fatura aponta para um contrato mas não para o item do contrato, então o custo dessa receita não pode ser calculado. Peça ao suporte para corrigir a linha {lineId} da fatura.',
      en: 'An invoice line points to a contract but not to its contract item, so the cost of this revenue cannot be computed. Ask support to fix invoice line {lineId}.',
    },
    COST_RECOGNITION_SOURCE_JOURNAL_NOT_FOUND: {
      ptBR: 'O lançamento de receita usado para provisionar o custo não foi encontrado ou não está lançado. Atualize a página e tente de novo.',
      en: 'The revenue journal used to provision the cost was not found or is not posted. Refresh and try again.',
    },
    COST_LINE_PROVISION_WITHOUT_ENTRIES: {
      ptBR: 'A linha está marcada como "Serviço de custo", mas nenhuma provisão foi escolhida. Use a baixa automática, escolha as provisões em "Ajustar" ou mude o destino para despesa.',
      en: 'The line is marked "Cost service" but no provision is selected. Use automatic relief, choose the provisions in "Adjust", or change the destination to expense.',
    },
    COST_MATCH_EXCEEDS_BILL_LINE: {
      ptBR: 'A soma dos valores aplicados nas provisões passa o valor da linha da conta. Reduza os valores aplicados até no máximo o valor da linha.',
      en: 'The amounts applied to provisions add up to more than the bill line. Reduce them to at most the line amount.',
    },
    COST_MATCH_EXCEEDS_ENTRY_BALANCE: {
      ptBR: 'O valor aplicado passa o saldo em aberto da provisão[[ {provisionLabel}]] (já descontado o que outras linhas reservaram)[[: ela tem {openAmount} livre e a linha aplica {appliedAmount}]]. Reduza o valor aplicado até o saldo livre, escolha outra provisão ou use "Voltar ao automático" para a linha baixar as provisões abertas mais antigas.',
      en: 'The amount applied is more than the provision[[ {provisionLabel}]] still has open (after what other lines reserved)[[: it has {openAmount} free and the line applies {appliedAmount}]]. Reduce the amount to the free balance, choose another provision, or use "Back to automatic" so the line relieves the oldest open provisions.',
    },
    COST_MATCH_VENDOR_MISMATCH: {
      ptBR: 'A provisão escolhida é devida a outro fornecedor, não ao fornecedor desta conta. Escolha uma provisão do mesmo fornecedor.',
      en: 'The selected provision is owed to another vendor, not the vendor of this bill. Choose a provision of the same vendor.',
    },
    COST_ENTRY_SCHEDULED_NOT_DUE: {
      ptBR: 'Este mês ainda não venceu. Para parar o plano, estorne a receita do contrato.',
      en: 'This month is not due yet. To stop the plan, reverse the contract revenue.',
    },
    COST_ENTRY_NOT_OPEN: {
      ptBR: 'A provisão escolhida não está mais em aberto (foi liquidada, estornada ou encerrada). Atualize a lista e escolha outra.',
      en: 'The selected provision is no longer open (it was settled, reversed or closed). Refresh the list and choose another.',
    },
    COST_MATCH_VARIANCE_OUT_OF_TOLERANCE: {
      ptBR: 'A diferença entre a linha da conta e as provisões passa a tolerância do serviço de custo. Confirme a variação para seguir, ou ajuste a seleção.',
      en: 'The difference between the bill line and the provisions is beyond the Cost Service tolerance. Confirm the variance to continue, or adjust the selection.',
    },
    COST_LINE_DESTINATION_NOT_AVAILABLE: {
      ptBR: 'Esse destino de linha ainda não está disponível nesta versão. Use "Despesa" ou "Serviço de custo".',
      en: 'That line destination is not available in this release yet. Use "Expense" or "Cost service".',
    },
    COST_MATCH_NOT_FOUND: {
      ptBR: 'A baixa de provisão não foi encontrada ou já foi desfeita. Atualize a conta e tente de novo.',
      en: 'The provision match was not found or was already undone. Refresh the bill and try again.',
    },
    COST_MATCH_UNDO_REASON_REQUIRED: {
      ptBR: 'Informe o motivo para desfazer a baixa de provisão. Ele fica registrado no histórico da conta.',
      en: 'Give a reason to undo the provision match. It is kept in the bill history.',
    },
    AP_BILL_NOT_EDITABLE: {
      ptBR: 'O destino e as provisões de uma linha só podem mudar enquanto a conta está em rascunho. Para uma conta já aprovada, use a baixa posterior ou desfaça a baixa.',
      en: 'A line destination and its provisions can only change while the bill is a draft. For an approved bill, match it later or undo the match.',
    },
    AP_BILL_GENERATED_LINE_HEADER_LOCKED: {
      ptBR: 'Esta conta já saiu do rascunho e tem uma linha gerada pelo serviço de custo. O valor, a moeda, o fornecedor e a entidade não podem mudar agora, porque a linha não acompanha mais a conta. Se a conta aguarda aprovação, peça a um aprovador para rejeitá-la, corrija em rascunho e envie de novo. Se a conta já foi aprovada ou paga, cancele-a e lance uma conta nova com os dados corretos.',
      en: 'This bill is past draft and has a line generated by the cost service. Its amount, currency, vendor and entity cannot change now, because the line no longer follows the bill. If the bill is waiting for approval, ask an approver to reject it, fix it as a draft and send it again. If the bill is already approved or paid, void it and enter a new bill with the correct data.',
    },
    AP_BILL_LAST_LINE_ITEM: {
      ptBR: 'Este é o único item da nota e não pode ser removido. Uma conta precisa de pelo menos um item; edite o item em vez de removê-lo.',
      en: 'This is the only line on the bill and cannot be removed. A bill needs at least one line; edit it instead.',
    },
    AP_BILL_LINE_ITEMS_LOCKED: {
      ptBR: 'Os itens da nota só podem ser incluídos, alterados ou removidos enquanto a conta está em rascunho, enviada, em aprovação ou rejeitada, e esta conta já passou da aprovação. Para corrigir os itens, estorne o lançamento da conta (ou anule a conta) e lance de novo.',
      en: 'Bill lines can only be added, changed or removed while the bill is a draft, submitted, in approval or rejected, and this bill is past approval. To fix its lines, reverse the bill posting (or void the bill) and post it again.',
    },
    AP_BILL_LINE_ITEM_HAS_HISTORY: {
      ptBR: 'Este item já tem histórico contábil (baixa de provisão, reserva ou lançamento) e não pode ser removido. Desfaça a baixa ou estorne o lançamento antes.',
      en: 'This line already has accounting history (a provision match, a reservation or a journal) and cannot be removed. Undo the match or reverse the journal first.',
    },
    COST_LINE_NOT_FOUND: {
      ptBR: 'A conta ou a linha da conta não foi encontrada. Atualize a tela e tente de novo.',
      en: 'The bill or the bill line was not found. Refresh and try again.',
    },
    COST_MATCH_CURRENCY_MISMATCH: {
      ptBR: 'A provisão escolhida está em outra moeda que a linha da conta. Escolha uma provisão na mesma moeda da conta.',
      en: 'The selected provision is in another currency than the bill line. Choose a provision in the bill currency.',
    },
    COST_MATCH_SELECTION_INVALID: {
      ptBR: 'A seleção de provisões não é válida: ela está vazia, repete uma provisão ou tem um valor que não é positivo. Corrija e tente de novo.',
      en: 'The provision selection is not valid: it is empty, repeats a provision or has an amount that is not positive. Fix it and try again.',
    },
    COST_MATCH_SET_NOT_FOUND: {
      ptBR: 'O conjunto sugerido não está mais entre as sugestões desta linha (os saldos mudaram). Peça as sugestões de novo e escolha outra vez.',
      en: 'The suggested set is no longer among the suggestions for this line (balances changed). Ask for suggestions again and choose again.',
    },
    COST_LINE_CLASSIFICATION_REQUIRED: {
      ptBR: 'Esta conta mistura linhas de serviço de custo com linhas de despesa, então cada linha precisa da sua própria classificação. Em Classificação, escolha "Por item da nota" e classifique cada linha.',
      en: 'This bill mixes cost-service lines with expense lines, so each line needs its own classification. In Classification, choose "Per invoice item" and classify each line.',
    },
    COST_LINE_SHARED_CLASSIFICATION_MULTIPLE: {
      ptBR: 'Todas as linhas desta conta são de serviço de custo, mas a conta tem mais de uma classificação para a conta inteira, então não dá para saber qual vale para cada linha. Em Classificação, deixe uma classificação só ou use "Por item da nota" e classifique cada linha.',
      en: 'Every line of this bill is a cost-service line, but the bill has more than one whole-bill classification, so it is not clear which one each line takes. In Classification, keep a single classification or use "Per invoice item" and classify each line.',
    },
    COST_LINE_SHARED_CLASSIFICATION_SUM_MISMATCH: {
      ptBR: 'As linhas desta conta somam[[ {lineTotal}]], mas o total da conta é[[ {billTotal}]], então a classificação da conta inteira não pode ser dividida entre as linhas. Ajuste o total da conta ou o valor das linhas para que fechem.',
      en: 'The lines of this bill add up to[[ {lineTotal}]], but the bill total is[[ {billTotal}]], so the whole-bill classification cannot be split across the lines. Adjust the bill total or the line amounts so they match.',
    },
    COST_LINE_POSTING_INCOMPATIBLE: {
      ptBR: 'Uma linha de serviço de custo não pode ser lançada junto com diferimento, imobilização, arrendamento, projeto, rateio de colaborador, reembolso de cliente ou créditos de PIS/COFINS.[[ Esta conta também tem {conflictLabel}.]] Retire o outro tratamento da conta ou mude a linha para despesa.',
      en: 'A line that pays a provision cannot post together with a deferral, a capitalisation, a lease, a project, a collaborator split, a client reimbursement or PIS/COFINS credits.[[ This bill also has {conflictLabelEn}.]] Remove the other treatment from the bill or make the line an expense.',
    },
    COST_MATCH_ACCOUNT_NOT_MAPPED: {
      ptBR: 'A conta da provisão, da variação de custo ou da variação cambial não tem conta correspondente no plano do livro. Mapeie a conta no plano de contas e tente de novo.',
      en: 'The provision, cost variance or FX variance account has no matching account in the book chart. Map the account in the chart of accounts and try again.',
    },
    COST_MATCH_PERIOD_NOT_OPEN: {
      ptBR: 'Não foi possível lançar a baixa de provisão (ou desfazê-la): o período da data de hoje não está aberto em um dos livros da conta. Reabra o período e tente de novo.',
      en: "The provision match (or its undo) could not be posted: today's period is not open in one of the bill's books. Reopen the period and try again.",
    },
    COST_PREPAID_RECLASS_NO_OPEN_PERIOD: {
      ptBR: 'Não foi possível lançar a baixa da despesa antecipada: nenhum período está aberto de {journalDate} até o mês atual[[ no livro {bookName}]]. Abra o período[[ {journalMonth}]][[ do livro {bookName}]] (ou um mês seguinte) e tente de novo.',
      en: 'The prepaid expense reclass could not be posted: no period is open from {journalDate} through the current month[[ in book {bookName}]]. Open the period[[ {journalMonth}]][[ of book {bookName}]] (or a later month) and try again.',
    },
    COST_MATCH_BILL_NOT_POSTED: {
      ptBR: 'A baixa posterior só vale para uma conta aprovada e lançada. Aprove a conta ou escolha as provisões antes de enviá-la para aprovação.',
      en: 'A later match only applies to a bill that is approved and posted. Approve the bill, or choose the provisions before sending it to approval.',
    },
    COST_MATCH_AFTER_APPROVAL_UNAVAILABLE: {
      ptBR: 'Esta conta já foi aprovada. A baixa posterior das provisões desta linha é feita na tela da conta: abra a conta e escolha as provisões lá.',
      en: 'This bill is already approved. Match the provisions of this line on the bill screen: open the bill and choose the provisions there.',
    },
    COST_MATCH_LINE_SETTLED_BY_ACCRUAL: {
      ptBR: 'Esta conta já baixou uma despesa provisionada (provisão de despesa recorrente), então a mesma despesa não pode também baixar uma provisão de custo. Desfaça aquela baixa primeiro.',
      en: 'This bill already settled an expense accrual, so the same expense cannot also relieve a cost provision. Undo that settlement first.',
    },
    COST_REVERSAL_PERIOD_NOT_OPEN: {
      ptBR: 'Não foi possível estornar o custo provisionado desta receita: o período da data do estorno não está aberto. Reabra o período ou escolha uma data em período aberto e tente de novo.',
      en: 'The provisioned cost of this revenue could not be reversed: the period of the reversal date is not open. Reopen the period or pick a date in an open period and try again.',
    },
    COST_REVERSAL_ACCOUNT_NOT_MAPPED: {
      ptBR: 'Não foi possível estornar o custo provisionado desta receita: uma conta do custo (custo, provisão ou variação cambial) não tem correspondente no plano de contas do livro na data do estorno. Mapeie a conta e tente de novo.',
      en: "The provisioned cost of this revenue could not be reversed: an account of the cost (cost, provision or FX variation) has no counterpart in the book's chart of accounts at the reversal date. Map the account and try again.",
    },
    COST_PROVISION_CLOSE_REASON_REQUIRED: {
      ptBR: 'Informe o motivo para encerrar a provisão (por exemplo, "fornecedor não cobrará"). Ele fica registrado na provisão e na auditoria.',
      en: 'Give a reason to close the provision (for example, "vendor will not bill"). It is kept on the provision and in the audit log.',
    },
    COST_PROVISION_CLOSE_PERIOD_NOT_OPEN: {
      ptBR: 'Não foi possível encerrar a provisão: o encerramento é lançado com a data de hoje e o período de hoje não está aberto neste livro. Abra o período atual e tente de novo.',
      en: "The provision could not be closed: the close-out posts with today's date and today's period is not open in this book. Open the current period and try again.",
    },
    COST_PROVISION_CLOSE_RESERVED_BY_BILL: {
      ptBR: 'Não foi possível encerrar a provisão: uma conta a pagar ainda em aberto (rascunho, em aprovação ou rejeitada) a escolheu para baixa. Tire a provisão dessa conta ou conclua a conta e tente de novo.',
      en: 'The provision could not be closed: a bill still in progress (draft, in approval or rejected) selected it for a match. Remove the provision from that bill or finish the bill and try again.',
    },
    COST_PROVISION_CLOSE_ACCOUNT_NOT_MAPPED: {
      ptBR: 'Não foi possível encerrar a provisão: uma conta do custo (custo, provisão ou variação cambial) não tem correspondente no plano de contas vigente hoje neste livro. Mapeie a conta e tente de novo.',
      en: "The provision could not be closed: an account of the cost (cost, provision or FX variation) has no counterpart in the book's chart of accounts in force today. Map the account and try again.",
    },
    COST_MATCH_RELIEF_REDUCED_BY_REVERSAL: {
      ptBR: 'Esta conta tem uma linha cuja baixa de provisão foi reduzida pelo estorno de uma receita, então ela não pode ser reaberta, editada nem reclassificada assim. Desfaça a baixa de provisão dessa linha primeiro (ou cancele a conta) e tente de novo.',
      en: "A line of this bill had its provision match reduced by a revenue reversal, so the bill cannot be reopened, edited or reclassified as it is. Undo that line's provision match first (or void the bill) and try again.",
    },
    COST_MATCH_RELIEF_BEFORE_RECOGNITION: {
      ptBR: 'A provisão de custo selecionada ainda não foi lançada no razão, então não há saldo para baixar. Lance os custos pendentes primeiro ou lance a linha como despesa.',
      en: 'The selected cost provision has not posted to the ledger yet, so there is no balance to relieve. Post the pending costs first or post the line as an expense.',
    },
    COST_SERVICE_PREPAID_ACCOUNT_NOT_CONFIGURED: {
      ptBR: 'Esta conta quita uma provisão de custo que só é lançada depois da data da conta, então essa parte fica em despesas antecipadas até a provisão ser lançada. Não há conta de ativo configurada para "Despesas antecipadas — custos de serviços" no mapeamento de contas desta entidade. Configure em Configurações → Contábil & Fiscal → Mapeamento de contas → Contas a pagar → Despesas antecipadas — custos de serviços e aprove de novo.',
      en: 'This bill pays a cost provision that posts after the bill date, so that part sits on prepaid expenses until the provision posts. No asset account is set for "Prepaid expenses — service costs" in this entity\'s account mapping. Set it in Settings → Accounting & Tax → Account mapping → Accounts payable → Prepaid expenses — service costs and approve again.',
    },
    COST_AWAITING_SELECTION_INVALID: {
      ptBR: 'A provisão esperada escolhida não serve para esta linha: o serviço de custo é de outra entidade, de outro fornecedor, em outra moeda ou não está ativo. Escolha um serviço de custo deste fornecedor e desta entidade e tente de novo.',
      en: 'The expected provision you chose does not fit this line: its Cost Service belongs to another entity or vendor, is in another currency, or is not active. Choose a Cost Service of this vendor and entity and try again.',
    },
    COST_AWAITING_RESERVATION_NOT_FOUND: {
      ptBR: 'Não encontramos o valor antecipado aguardando provisão. Atualize a tela e tente de novo.',
      en: 'The prepaid amount awaiting its provision was not found. Refresh the page and try again.',
    },
    COST_AWAITING_RESERVATION_NOT_OPEN: {
      ptBR: 'Este valor antecipado não está mais aguardando provisão: ele já foi baixado por uma provisão, desfeito, encerrado ou cancelado com a conta. Atualize a tela para ver a situação atual.',
      en: 'This prepaid amount is no longer awaiting its provision: a provision already consumed it, or it was undone, closed or voided with the bill. Refresh the page to see where it stands.',
    },
    COST_AWAITING_MONTH_ALREADY_PROVISIONED: {
      ptBR: 'Este mês já tem provisão lançada para este serviço de custo e fornecedor. Em vez do mês previsto, selecione a provisão lançada na lista e tente de novo.',
      en: 'This month already has a posted provision for this Cost Service and vendor. Instead of the expected month, select the posted provision in the list and try again.',
    },
    COST_AWAITING_MONTH_WILL_NOT_POST: {
      ptBR: 'Nenhuma provisão será lançada neste mês para este serviço de custo: o mês está fora do período ativo do serviço, a parcela do plano foi cancelada por um estorno da receita ou o valor fixo já foi lançado para todos os contratos. Escolha outro mês ou lance a linha como despesa.',
      en: "No provision will ever post in this month for this Cost Service: the month is outside the service's active period, the plan month was cancelled by a revenue reversal, or the fixed amount already posted for every contract. Choose another month or post the line as an expense.",
    },
    COST_LINE_SERVICE_CHOICE_NOT_AVAILABLE: {
      ptBR: 'A escolha de serviço de custo por linha só existe quando o fornecedor tem dois ou mais serviços de custo ativos nesta entidade, e o serviço escolhido precisa ser um deles. Atualize a conta e escolha de novo.',
      en: 'A per-line cost service choice only exists when the vendor has two or more active cost services in this entity, and the chosen service must be one of them. Refresh the bill and choose again.',
    },
    COST_LINE_SPLIT_INVALID: {
      ptBR: 'A divisão da linha entre serviços de custo não é válida: ela precisa de pelo menos dois serviços diferentes, cada um com um valor positivo. Corrija e tente de novo.',
      en: 'The split of the line across cost services is not valid: it needs at least two different services, each with a positive amount. Fix it and try again.',
    },
    COST_LINE_FIFO_NO_COST_SERVICE: {
      ptBR: 'Este fornecedor não tem serviço de custo ativo nesta entidade na data da conta, então não há provisão para baixar. Lance a linha como despesa ou ative o serviço de custo.',
      en: 'This vendor has no active cost service in this entity on the bill date, so there is no provision to relieve. Post the line as an expense or activate the cost service.',
    },
    COST_LINE_FIFO_EXCESS_NO_MONTH: {
      ptBR: 'O valor da linha passa o total das provisões abertas deste serviço de custo[[ em {excessAmount}]], nenhum mês seguinte do serviço recebe a diferença e a classificação da linha não tem uma conta de despesa ativa e lançável para recebê-la. Classifique a linha numa conta de despesa (ou custo) ativa e lançável, ou confira o valor da linha.',
      en: 'The line amount is more than the open provisions of this cost service[[ by {excessAmount}]], no later month of the service takes the difference, and the line classification has no active, postable expense account to take it. Classify the line on an active, postable expense (or cost) account, or check the line amount.',
    },
    COST_LINE_FIFO_SERVICE_INACTIVE_OPEN_PROVISIONS: {
      ptBR: 'O serviço de custo {costServiceName}[[ terminou em {endedOn}]][[ está pausado{paused}]], mas tem {openAmount} de provisão aberta. A linha vai baixar essa provisão.',
      en: 'The cost service {costServiceName}[[ ended on {endedOn}]][[ is paused{paused}]], but has {openAmount} of open provisions. The line will relieve them.',
    },
    COST_LINE_FIFO_EXCESS_TO_EXPENSE: {
      ptBR: '{excessAmount} passa das provisões abertas e vai como despesa em {expenseAccount}.',
      en: '{excessAmount} is more than the open provisions and posts as an expense to {expenseAccount}.',
    },
    COST_LINE_FIFO_EXCESS_TO_VARIANCE: {
      ptBR: '{varianceAmount} passa da provisão de {costServiceName} de {competencyMonth}, um mês já provisionado, e vai como variação do serviço de custo[[ (tolerância {tolerance})]]. Confira se a conta não cobra o mesmo serviço duas vezes.',
      en: '{varianceAmount} is more than the {costServiceName} provision for {competencyMonth}, a month already provisioned, and posts to the cost service variance[[ (tolerance {tolerance})]]. Check that the bill does not charge the same service twice.',
    },
    COST_LINE_FIFO_VARIANCE_ACCOUNT_NOT_CONFIGURED: {
      ptBR: '{varianceAmount} passa da provisão de {costServiceName} de um mês já provisionado e vai como variação, mas essa provisão não tem conta de variação. Separe a diferença em outra linha, como despesa, e envie de novo; para as próximas provisões, cadastre a conta de variação no serviço de custo.',
      en: '{varianceAmount} is more than the {costServiceName} provision for a month already provisioned and posts as variance, but that provision has no variance account. Move the difference to another line, as an expense, and send again; for future provisions, set the variance account on the cost service.',
    },
    COST_LINE_SPLIT_SUM_MISMATCH: {
      ptBR: 'A soma das partes da divisão não é igual ao valor da linha da conta. Ajuste os valores para que fechem exatamente com a linha.',
      en: 'The split parts do not add up to the bill line amount. Adjust the amounts so they match the line exactly.',
    },
    COST_LINE_SPLIT_PART_EXCEEDED: {
      ptBR: 'As provisões escolhidas para um serviço de custo passam o valor da parte desse serviço na divisão. Reduza os valores aplicados ou aumente a parte do serviço.',
      en: "The provisions chosen for a cost service add up to more than that service's part of the split. Reduce the amounts applied or raise the service's part.",
    },
    COST_LINE_SERVICE_CHOICE_MISMATCH: {
      ptBR: 'Uma provisão escolhida pertence a um serviço de custo que ficou fora da escolha desta linha. Escolha provisões do serviço selecionado ou mude a escolha para "Todos".',
      en: 'A chosen provision belongs to a cost service this line\'s choice leaves out. Choose provisions of the selected service or change the choice to "All".',
    },
    COST_LINE_SERVICE_INACTIVE: {
      ptBR: 'Um dos serviços de custo escolhidos para esta linha não está ativo na data da conta. Escolha um serviço de custo ativo do fornecedor ou ative o serviço e tente de novo.',
      en: 'One of the cost services chosen for this line is not active on the bill date. Choose an active cost service of the vendor, or activate the service and try again.',
    },
    COST_LINE_SERVICE_OTHER_VENDOR: {
      ptBR: 'Um dos serviços de custo escolhidos para esta linha é de outro fornecedor ou de outra entidade. Escolha apenas serviços de custo do fornecedor desta conta.',
      en: "One of the cost services chosen for this line belongs to another vendor or another entity. Choose only cost services of this bill's vendor.",
    },
    COST_REVERSAL_RETRY_BILL_RELIEF_CHANGED: {
      ptBR: 'Uma conta a pagar baixou esta provisão de custo enquanto a receita era estornada, então o estorno não foi feito para não desfazer a baixa errada. Nada foi alterado. Tente o estorno de novo.',
      en: 'A vendor bill relieved this cost provision while the revenue was being reversed, so the reversal stopped rather than undo the wrong relief. Nothing changed. Try the reversal again.',
    },
  });

/**
 * D45c — what COST_LINE_POSTING_INCOMPATIBLE names as the other treatment of the bill
 * (`conflict` in the error's details), in both locales. Example: DEFERRAL → "um diferimento", so
 * the copy reads "Esta conta também tem um diferimento."
 */
export const COST_LINE_POSTING_CONFLICT_LABELS: Readonly<
  Record<string, { ptBR: string; en: string }>
> = Object.freeze({
  DEFERRAL: { ptBR: 'um diferimento', en: 'a deferral' },
  CLIENT_REIMBURSABLE: { ptBR: 'reembolso de cliente', en: 'a client reimbursement' },
  CAPITALIZATION: { ptBR: 'uma imobilização', en: 'a capitalisation' },
  LEASE_SETTLEMENT: { ptBR: 'uma parcela de arrendamento', en: 'a lease instalment' },
  PROJECT_ROUTING: { ptBR: 'um rateio para projeto', en: 'a project routing' },
  COLLABORATOR_SPLIT: { ptBR: 'um rateio de colaborador', en: 'a collaborator split' },
  TAX_CREDITS: { ptBR: 'créditos de PIS/COFINS', en: 'PIS/COFINS credits' },
  STATUTORY_DOCUMENT: {
    ptBR: 'origem em uma guia de tributo',
    en: 'a statutory tax document as its source',
  },
  GL_POSTING_SKIPPED: { ptBR: 'o lançamento contábil desligado', en: 'GL posting turned off' },
  PROVISION_MODE_NONE: {
    ptBR: 'a provisão deixada para a conciliação do pagamento',
    en: 'its provision left to the payment reconciliation',
  },
  PROVISION_MODE_STATUTORY_BYPASS_RECLASSIFICATION: {
    ptBR: 'o tratamento de guia de tributo (reclassificação)',
    en: 'the statutory treatment (reclassification)',
  },
  PROVISION_MODE_STATUTORY_AUTO_PROVISION: {
    ptBR: 'o tratamento de guia de tributo (provisão automática)',
    en: 'the statutory treatment (automatic provision)',
  },
});

/** D45c: the details of COST_LINE_POSTING_INCOMPATIBLE, with the conflict named when known. */
export function costLinePostingConflictDetails(conflict: string): Record<string, string> {
  const label = COST_LINE_POSTING_CONFLICT_LABELS[conflict];
  return label ? { conflict, conflictLabel: label.ptBR, conflictLabelEn: label.en } : { conflict };
}

/**
 * Fills `{name}` placeholders of a copy from the error's details. A YYYY-MM-DD day reads
 * DD/MM/YYYY in pt-BR and stays ISO in en. A placeholder with no detail is left as it is, except
 * inside an optional segment `[[ … ]]` (D45c): the segment is dropped unless every placeholder in
 * it is filled. Example: "Abra o período[[ {journalMonth}]]." with no `journalMonth` →
 * "Abra o período."; with "10/2026" → "Abra o período 10/2026.".
 */
export function fillCostServiceCopy(
  template: string,
  details: Readonly<Record<string, unknown>>,
  locale: 'ptBR' | 'en',
): string {
  const fill = (text: string) =>
    text.replace(/\{(\w+)\}/g, (match, name: string) => {
      const value = details[name];
      if (typeof value !== 'string' && typeof value !== 'number') return match;
      const raw = String(value);
      const isoDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
      if (isoDay && locale === 'ptBR') return `${isoDay[3]}/${isoDay[2]}/${isoDay[1]}`;
      return raw;
    });
  // D45c: `[[ … ]]` is an optional segment, kept only when every placeholder in it is filled.
  const withSegments = template.replace(/\[\[([\s\S]*?)\]\]/g, (_match, segment: string) => {
    const filled = fill(segment);
    return /\{\w+\}/.test(filled) ? '' : filled;
  });
  return fill(withSegments);
}

/**
 * Thrown by Cost Services code. `message` is the en copy, `userMessage` the pt-BR copy for the
 * operator, `details` carries ids and the offending knob (never PII). Classify by `code`.
 */
export class CostServiceError extends Error {
  readonly code: CostServiceErrorCode;
  readonly userMessage: string;
  readonly details: Readonly<Record<string, unknown>>;

  constructor(code: CostServiceErrorCode, details: Record<string, unknown> = {}) {
    super(fillCostServiceCopy(COST_SERVICE_ERROR_MESSAGES[code].en, details, 'en'));
    this.name = 'CostServiceError';
    this.code = code;
    this.userMessage = fillCostServiceCopy(COST_SERVICE_ERROR_MESSAGES[code].ptBR, details, 'ptBR');
    this.details = Object.freeze({ ...details });
  }
}

export function isCostServiceError(error: unknown): error is CostServiceError {
  return error instanceof CostServiceError;
}
